import dayjs from '@utils/dayjs';

// i18n-ns: allDeltas (callers pass their allDeltas-namespace `t`)

export const getFrequencyOptions = (t) => [
    {
        value: 'week',
        text: t('serie.frequency.weekly'),
    },
    {
        value: 'month',
        text: t('serie.frequency.monthly'),
    },
    {
        value: 'year',
        text: t('serie.frequency.yearly'),
    },
];

export const getMonths = (t) => [...Array(12).keys()].map((i) => ({ value: i + 1, text: t(`month.${i + 1}`) }));

export const getWeekDays = (t) => [
    {
        value: 1,
        text: t('weekday.monday'),
        symbol: t('weekday.symbol.monday'),
    },
    {
        value: 2,
        text: t('weekday.tuesday'),
        symbol: t('weekday.symbol.tuesday'),
    },
    {
        value: 3,
        text: t('weekday.wednesday'),
        symbol: t('weekday.symbol.wednesday'),
    },
    {
        value: 4,
        text: t('weekday.thursday'),
        symbol: t('weekday.symbol.thursday'),
    },
    {
        value: 5,
        text: t('weekday.friday'),
        symbol: t('weekday.symbol.friday'),
    },
    {
        value: 6,
        text: t('weekday.saturday'),
        symbol: t('weekday.symbol.saturday'),
    },
    {
        value: 0,
        text: t('weekday.sunday'),
        symbol: t('weekday.symbol.sunday'),
    },
];

export const getRepetitionOptionsByFrequency = (frequency, t) =>
    frequency === 'year' ? getMonths(t) : [...Array(100).keys()].map((x) => ({ value: x + 1, text: x + 1 }));

function getDaysOfWeekInMonth(day, month, year, dayOfWeek) {
    const days = [];
    const d = new Date(year, month - 1, 1);
    while (d.getMonth() === month - 1) {
        if (d.getDay() === dayOfWeek) {
            days.push(d.getDate());
        }
        d.setDate(d.getDate() + 1);
    }
    return days;
}

export const getRepetitionsLabelByFrequency = (frequency, t) => {
    switch (frequency) {
        case 'week':
            return t('serie.weeksLabel');
        case 'month':
            return t('serie.monthsLabel');
        default:
            return '';
    }
};

export const showWeekDaysRepetition = (frequency) => frequency === 'week';

export const getOrdinalNumbersForDayInMonth = (eventStart) => {
    const daysOfWeekInMonth = getDaysOfWeekInMonth(
        dayjs(eventStart).date(),
        dayjs(eventStart).month() + 1,
        dayjs(eventStart).year(),
        dayjs(eventStart).day()
    );

    const ordinalNumbers = [];
    for (let i = 0; i < daysOfWeekInMonth.length; i++) {
        switch (i) {
            case 0:
                ordinalNumbers.push('Ersten');
                break;
            case 1:
                ordinalNumbers.push('Zweiten');
                break;
            case 2:
                ordinalNumbers.push('Dritten');
                break;
            case 3:
                ordinalNumbers.push(daysOfWeekInMonth.length === 5 ? 'Vierten' : 'Letzten');
                break;
            case 4:
                ordinalNumbers.push('Letzten');
                break;
            default:
                break;
        }
    }

    return ordinalNumbers;
};

// The ordinal values (Ersten, Zweiten, ...) are persisted keys consumed by the
// backend, so only their displayed label is translated here.
//
// Returns '' rather than the falsy input so the result is always a string:
// callers case-shift it (EventRecurringInfo lowercases it for use mid-sentence,
// while the dropdown keeps title case). Today no caller can reach that branch
// with a falsy ordinal — repetitionIntervalType is derived by
// getRepetitionIntervalType below, which only reports DAY_ORDINAL_NUMBER_-
// REPETITION when the ordinal is set — so this is defensive, not a bug fix.
export const getOrdinalNumberLabel = (ordinalNumber, t) => (ordinalNumber ? t(`serie.ordinal.${ordinalNumber}`) : '');

export const getDayOrdinalNumberByDate = (date) => {
    date = dayjs(date);
    const dateNumber = date.date();
    switch (true) {
        case dateNumber <= 7:
            return 'Ersten';
        case dateNumber > 7 && dateNumber <= 14:
            return 'Zweiten';
        case dateNumber > 14 && dateNumber <= 21:
            return 'Dritten';
        case dateNumber > 21 && dateNumber <= 28:
            return date.clone().add(7, 'days').month() !== date.month() ? 'Letzten' : 'Vierten';
        case dateNumber >= 29:
            return 'Letzten';
        default:
            return 'Ersten';
    }
};

// Stable default value so callers can reference it without needing a `t` instance.
export const DEFAULT_RECURRING_OPTION = 'nonrecurrent';

export const getRecurringOptions = (t) => [
    {
        value: DEFAULT_RECURRING_OPTION,
        text: t('serie.recurring.none'),
    },
    {
        value: 'week',
        text: t('serie.recurring.weekly'),
    },
    {
        value: 'month',
        text: t('serie.recurring.monthly'),
    },
    {
        value: 'year',
        text: t('serie.recurring.yearly'),
    },
];

export const RepetitionIntervalType = {
    DAY_NUMBER_REPETITION: 'DAY_NUMBER_REPETITION',
    DAY_ORDINAL_NUMBER_REPETITION: 'DAY_ORDINAL_NUMBER_REPETITION',
};

export const getRepetitionIntervalType = (serie) => {
    if (!serie || (!serie.repetitionIntervalDayOrdinalNumber && !serie.repetitionIntervalDayNumber)) return null;
    if (serie.repetitionIntervalDayOrdinalNumber) {
        return RepetitionIntervalType.DAY_ORDINAL_NUMBER_REPETITION;
    }
    if (serie.repetitionIntervalDayNumber) return RepetitionIntervalType.DAY_NUMBER_REPETITION;
};
